/**
 * @file 编辑面试任务全屏弹窗
 */
import { Button, State, Toast } from '@seiue/ui'
import { pick } from '@seiue/util'
import { Form } from 'antd'
import moment from 'moment'
import { FC, useEffect, useRef, useState } from 'react'

import {
  InterviewActivityTaskResultSettingsReq,
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  interviewApi,
} from 'packages/sdks-next/growp'

import { CommonBasicInfoRef, SubmitValues } from '@/components/CommonBasicInfo'
import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'

import { EditTaskModalProps } from '../types'

import { BasicInfo } from './BasicInfo'

/**
 * 编辑面试任务弹窗
 *
 * @param param0 - 参数
 * @param param0.onClose - 关闭弹窗
 * @param param0.taskId - 任务 ID
 * @param param0.activityId - 活动 ID
 * @param param0.activityTaskOptions - 活动任务选项
 * @returns 组件
 */
export const EditInterviewTaskModal: FC<EditTaskModalProps> = ({
  onClose,
  activityId,
  taskId,
  activityTaskOptions,
}) => {
  const isEdit = !!taskId

  const [form] = Form.useForm()
  const [title, setTitle] = useState<string>('')
  const [commonBasicInfo, setCommonBasicInfo] = useState<SubmitValues>({})
  const basicInfoRef = useRef<CommonBasicInfoRef>(null)

  const { data: editTask, loading } = interviewApi.useLoadInterviewActivityTask(
    {
      id: taskId || 0,
    },
    { disable: !taskId }
  )

  const filterTaskOptions =
    activityTaskOptions?.filter((o) => o.value !== taskId) || []

  const { api: createActivityTask } =
    interviewApi.useCreateInterviewActivityTask({
      reload: [activityApi.queryTasks, activityApi.loadActivity],
    })

  const { api: updateActivityTask } =
    interviewApi.useUpdateInterviewActivityTask({
      reload: [activityApi.queryTasks, activityApi.loadActivity],
    })

  useEffect(() => {
    form.resetFields()

    form.setFieldsValue({ ...editTask })
    setTitle(editTask?.name || '')

    setCommonBasicInfo({
      ...pick({ ...editTask }, [
        'endAt',
        'adminIds',
        'timedStartAt',
        'prevTaskIds',
      ]),
      startAt: editTask
        ? editTask.startAt
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      isShow: editTask?.isShow !== undefined ? editTask.isShow : true,
    })
  }, [editTask, form])

  if (loading) return <State.Loading />
  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || '未命名面试'}
          onClose={onClose}
        />
      }
      headerRight={
        <>
          <Button
            type="primary"
            onClick={async () => {
              form.submit()
              const commonRefInfo = basicInfoRef.current?.getBasicInfo()

              form.validateFields().then(async (values) => {
                const interviewResults =
                  values.interviewResults as InterviewActivityTaskResultSettingsReq[]
                const data = {
                  ...pick(values, ['name']),
                  ...(commonRefInfo ?? {}),
                  describetion: '',
                  activityId,
                  type: TaskTypeEnum.Interview,
                  status: TaskStatusEnum.Published,
                }
                if (isEdit) {
                  await updateActivityTask({
                    id: taskId,
                    payload: { ...data, interviewResults: interviewResults },
                  })
                } else {
                  const addRes = interviewResults.map((e) => e.interviewResult)
                  await createActivityTask({
                    ...data,
                    interviewResults: addRes,
                  })
                }

                Toast.success(isEdit ? '更新任务成功' : '保存任务成功')

                onClose()
              })
            }}
          >
            {isEdit ? '更新' : '确认'}
          </Button>
        </>
      }
      content={
        <ContentCard
          style={{ minHeight: 200 }}
          contentStyle={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px 32px',
          }}
        >
          <BasicInfo
            setTitle={setTitle}
            form={form}
            commonBasicInfo={commonBasicInfo}
            basicInfoRef={basicInfoRef}
            activityTaskOptions={filterTaskOptions}
            initResults={isEdit ? editTask?.interviewResults : undefined}
            openTypeDisabled={isEdit ? editTask?.isSubmit : false}
          />
        </ContentCard>
      }
    />
  )
}
